import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

const DEFAULT_TIMEOUT_SECS = 20;

export default function StudentOfGameModal({ drawnCards, allCards, onPick, timeoutSecs }) {
  const effectiveTimeout = timeoutSecs > 0 ? timeoutSecs : DEFAULT_TIMEOUT_SECS;
  const [selected, setSelected] = useState(null);
  const [timeLeft, setTimeLeft] = useState(effectiveTimeout);
  const autoFiredRef = useRef(false);

  const cardById = (id) => allCards?.find(c => c.id === id) || { id, name: id, description: "" };

  useEffect(() => {
    if (!drawnCards?.length) return;
    setTimeLeft(effectiveTimeout);
    autoFiredRef.current = false;
    const first = drawnCards[0];
    const id = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (!autoFiredRef.current) {
            autoFiredRef.current = true;
            onPick(first);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-slate-900 border border-blue-500/40 rounded-2xl p-5 w-full max-w-sm mx-4 shadow-2xl"
      >
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] font-black tracking-widest text-blue-400 uppercase">Student of the Game</p>
            <span className={`text-xs font-black tabular-nums ${timeLeft <= 5 ? "text-red-400 animate-pulse" : "text-slate-400"}`}>
              {timeLeft}s
            </span>
          </div>
          <p className="text-sm text-slate-400">Keep one card, discard the other.</p>
        </div>

        <div className="space-y-2 mb-5">
          {drawnCards.map((id) => {
            const card = cardById(id);
            const isSelected = selected === id;
            return (
              <button
                key={id}
                onClick={() => setSelected(id)}
                className={`w-full text-left rounded-xl border p-3 transition-all ${
                  isSelected
                    ? "border-blue-500/70 bg-blue-500/15"
                    : "border-slate-700 bg-slate-800/60 hover:border-slate-500"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-black text-white">{card.name}</span>
                  {isSelected && (
                    <span className="text-[8px] font-black px-1.5 py-0.5 rounded bg-blue-500/20 border border-blue-500/40 text-blue-300 uppercase tracking-wider">
                      Keep
                    </span>
                  )}
                </div>
                {card.description && (
                  <p className="text-xs text-slate-500 mt-1">{card.description}</p>
                )}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => selected && onPick(selected)}
          disabled={!selected}
          className="w-full py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-black text-sm transition disabled:opacity-40"
        >
          {selected ? "Keep This Card →" : "Select a card to keep"}
        </button>
      </motion.div>
    </div>
  );
}
